import { useFormik } from "formik";
import { FC, useEffect, useState } from "react"
import { SettingMultiPipelines } from "../../../interfaces/Settings";
import { useIntl } from "react-intl";
import useAccessToken from "../../../hooks/AccessToken";
import { useSettingsStore } from "../../../stores/SettingsStore";
import { usePipelineStore } from "../../../stores/PipelineStore";
import * as Yup from 'yup';
import { SelectOption } from "../../../interfaces/SelectOption";
import { getAllOption, getFilterDateOptions, getSourceFilterDateOptions } from "../../../functions/getOptions";
import { updateTemplateSettingPipeline } from "../../../api/SettingCRUD";
import Swal from "sweetalert2";
import clsx from "clsx";
import VirtualizedSelect, { VirtualizedSelectOption } from "../../Select/SelectVirtualized";
import { MasterCurrency } from "../../../interfaces/Currency";
import { useCurrencyStore } from "../../../stores/CurrencyStore";
import { Toggle } from "rsuite";
import { IPriceVariant } from "../../../interfaces/PriceVariant";
import { fetcherSWR } from "../../../functions/general";
import useSWR from "swr";

interface Props {
    data: SettingMultiPipelines
    dataMerchant: SelectOption[]
    dataProgres: SelectOption[]
    dataProduct: SelectOption[]
    dataSource: SelectOption[]
    dataRole: SelectOption[]
}

const API_URL = process.env.REACT_APP_API_URL

const PreferencePipeline: FC<Props> = ({ data, dataMerchant, dataProgres, dataProduct, dataSource, dataRole }) => {
    const intl = useIntl()
    const token = useAccessToken()
    const { settings } = useSettingsStore()
    const { multiCurrencies } = useCurrencyStore()
    const { multiPipelines, setMultiPipelines } = usePipelineStore()
    const [currencyOptions, setCurrencyOptions] = useState<SelectOption[]>([])

    const allOptionProgress = getAllOption(intl.formatMessage({ id: "FORM.LABEL.PROGRESS" }), intl)
    const allOptionProduct = getAllOption(intl.formatMessage({ id: "FORM.LABEL.PRODUCTS" }), intl)
    const allOptionMerchant = getAllOption(settings.merchant_title, intl)
    const allOptionSource = getAllOption(intl.formatMessage({ id: "FORM.LABEL.SOURCE" }), intl)
    const allOptionRole = getAllOption(intl.formatMessage({ id: "FORM.LABEL.ROLE" }), intl)
    const sourceFilterDateOption = getSourceFilterDateOptions(intl);
    const filterDateOption = getFilterDateOptions(intl);
    const formik = useFormik({
        initialValues: {
            value: data.name,
            currencyId: String(data.currencyId ?? '360'),
            merchantTemplateIds: data.merchantTemplates && data.merchantTemplates.length > 0 ? data.merchantTemplates.length === dataMerchant.filter(data => data.value !== 'all').length ? allOptionMerchant : data.merchantTemplates : allOptionMerchant,
            roleIds: data.roles && data.roles.length > 0 ? data.roles.length === dataRole.filter(data => data.value !== 'all').length ? allOptionRole : data.roles : allOptionRole,
            accessProgress: data.progress && data.progress.length > 0 ? data.progress.length === dataProgres.filter(data => data.value !== 'all').length ? allOptionProgress : data.progress : allOptionProgress,
            accessProduct: data.product && data.product.length > 0 ? data.product.length === dataProduct.filter(data => data.value !== 'all').length ? allOptionProduct : data.product : allOptionProduct,
            isInfiniteLog: data.isInfiniteLog ? true : false,
            isAllowAfterFailedLog: data.isAllowAfterFailedLog ? true : false,
            isAllowAfterSuccessLog: data.isAllowAfterSuccessLog ? true : false,
            isFeatureLabelEnabled: data.isFeatureLabelEnabled ? true : false,
            isFeatureCogsEnabled: data.isFeatureCogsEnabled ? true : false,
            sourceFilterDate: data.sourceFilterDate ?? 'NONE',
            filterDate: data.filterDate ?? 'NONE',
            leadSourcesId: data.leadSources && data.leadSources.length > 0 ? data.leadSources.length === dataSource.filter(data => data.value !== 'all').length ? allOptionSource : data.leadSources : allOptionSource,
            allowOptionalProducts: data.allowOptionalProducts ? true : false,
            isDisabledPriceProduct: data.isDisabledPriceProduct ? true : false,
            variantPriceId: data?.variantPriceId ?? null,
        },
        validationSchema: Yup.object().shape({
            value: Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.NAME" }) })).max(30, intl.formatMessage({ id: "FORM.VALIDATION.MAXIMAL" }, { value: '30' })),
            currencyId: Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.CURRENCY" }) })),
            merchantTemplateIds: Yup.array().min(0, intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: settings.merchant_title + " database" })).required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.PROGRESS" }) })).nullable(),
            roleIds: Yup.array().min(0, intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.ROLE" }) })).required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.ROLE" }) })).nullable(),
            accessProgress: Yup.array().min(1, intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.PROGRESS" }) })).required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.PROGRESS" }) })).nullable(),
            accessProduct: Yup.array().min(1, intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.PRODUCTS" }) })).required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.PROGRESS" }) })).nullable(),
            isInfiniteLog: Yup.boolean().required(intl.formatMessage({ id: "FORM.LABEL.SETTING_ADD_LEAD_LOG" })),
            isAllowAfterFailedLog: Yup.boolean().required(intl.formatMessage({ id: "FORM.LABEL.SETTING_ADD_LEAD_LOG_FAILED" })),
            isAllowAfterSuccessLog: Yup.boolean().required(intl.formatMessage({ id: "FORM.LABEL.SETTING_ADD_LEAD_LOG_SUCCESS" })),
            isFeatureLabelEnabled: Yup.boolean().required(intl.formatMessage({ id: "FORM.LABEL.SETTING_LABEL" })),
            isFeatureCogsEnabled: Yup.boolean().required(intl.formatMessage({ id: "FORM.LABEL.SETTING_COGS" })),
            sourceFilterDate: Yup.mixed().oneOf(['NONE', 'CREATED', 'STARTED', 'ENDED']).required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.NAME" }) })),
            filterDate: Yup.mixed().oneOf(['NONE', 'TODAY', 'YESTERDAY', 'LAST_WEEK', 'THIS_WEEK', 'LAST_MONTH', 'THIS_MONTH', 'LAST_YEAR', 'THIS_YEAR', 'STARTED', 'ENDED']),
            leadSourcesId: Yup.array().min(1, intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.SOURCE" }) })).required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.SOURCE" }) })).nullable(),
            allowOptionalProducts: Yup.boolean().required(intl.formatMessage({ id: "FORM.LABEL.SETTING_OPTIONAL_PRODUCT" })),
            isDisabledPriceProduct: Yup.boolean().required(intl.formatMessage({ id: "FORM.LABEL.SETTING_DISABLED_PRICE_PRODUCT" })),
        }),
        enableReinitialize: true,
        onSubmit: async (values, { setStatus, setSubmitting }) => {
            setSubmitting(true)

            const formattedSourceFilterDate = values.sourceFilterDate === 'NONE' ? null : values.sourceFilterDate;
            const formattedFilterDate = values.filterDate === 'NONE' ? null : values.filterDate;

            if (formattedSourceFilterDate !== null && formattedFilterDate === null) {
                formik.setFieldError('filterDate', intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "STUDIO.LABEL.RANGE_DATE_FORM" }) }));
                setSubmitting(false)
                return
            }

            let accessProgress = values.accessProgress.map(item => item?.value).filter((v): v is string => Boolean(v));
            let accessProduct = values.accessProduct.map(item => item?.value).filter((v): v is string => Boolean(v));
            let merchantTemplateIds = values.merchantTemplateIds.map(item => item?.value).filter((v): v is string => Boolean(v));
            let roleIds = values.roleIds.map(item => item?.value).filter((v): v is string => Boolean(v));
            let leadSourcesId = values.leadSourcesId.map(item => item?.value).filter((v): v is string => Boolean(v));

            try {
                const response =
                    await updateTemplateSettingPipeline(
                        token,
                        data.id,
                        values.value,
                        Number(values.currencyId),
                        merchantTemplateIds,
                        roleIds,
                        accessProgress,
                        accessProduct,
                        data.customFields,
                        values.isFeatureLabelEnabled,
                        values.isFeatureCogsEnabled,
                        leadSourcesId,
                        values.allowOptionalProducts,
                        values.isInfiniteLog,
                        values.isAllowAfterFailedLog,
                        values.isAllowAfterSuccessLog,
                        formattedSourceFilterDate,
                        formattedFilterDate,
                        values.variantPriceId,
                        values.isDisabledPriceProduct,
                    );

                if (response.data.success) {
                    Swal.fire({
                        icon: 'success',
                        heightAuto: false,
                        title: intl.formatMessage({ id: "FORM.VALIDATION.SAVE_SUCCESS" }),
                        timer: 2000,
                        timerProgressBar: true
                    });

                    setMultiPipelines(
                        multiPipelines.map(item =>
                            item.id === data.id
                                ? { ...response.data.data, label: response.data.data.name, value: response.data.data.id, isInfiniteLog: values.isInfiniteLog, isAllowAfterFailedLog: values.isAllowAfterFailedLog, isAllowAfterSuccessLog: values.isAllowAfterSuccessLog, sourceFilterDate: formattedSourceFilterDate, filterDate: formattedFilterDate }
                                : item
                        ));
                } else {
                    throw new Error('Gagal menyimpan data, mohon coba lagi.');
                }
            } catch (error: any) {
                const errorMessage = error?.response?.data?.message || error?.message || 'Terjadi kesalahan, mohon coba lagi.';
                Swal.fire({
                    icon: 'error',
                    heightAuto: false,
                    title: errorMessage,
                    confirmButtonText: 'Ok'
                });
            } finally {
                setSubmitting(false);
            }

        }
    })

    useEffect(() => {
        let data = multiCurrencies.map((currency: MasterCurrency) => {
            return {
                value: currency.id.toString(),
                label: currency.name + ' (' + currency.symbol + ')',
            }
        })

        setCurrencyOptions([...data])
    }, [multiCurrencies])


    const header = token ? { 'X-Auth-token': token } : {}

    const fetcherSWRWithTransformPriceVariantOption = (
        args: [string | undefined, typeof header, any]
    ): Promise<{ value: string; label: string }[]> =>
        fetcherSWR(args).then((data: IPriceVariant[]) =>
            data.map((sp) => ({
                value: sp.id,
                label: `${sp.variantPriceName}`,
            }))
        );

    const { data: _variantPriceOption = [], error: variantPriceError, isLoading: variantPriceIsLoading } = useSWR([`${API_URL}variant-prices`, header, {}], fetcherSWRWithTransformPriceVariantOption)

    return (
        <form onSubmit={formik.handleSubmit} noValidate>
            <div className="card-body p-0">
                <div className="row">
                    <span className="col-md-7 col-lg-9 col-xl-8 d-flex flex-column align-items-start">
                        <span className="fw-bolder fs-5 mb-0">{`${intl.formatMessage({ id: "FORM.LABEL.NAME" })} `}</span>
                        <span className="text-muted fs-6" style={{ maxWidth: '500px' }}>{intl.formatMessage({ id: "FORM.LABEL.DESC_SETTING_NAME_TEMPLATE" })}</span>
                    </span>
                    <div className="col-md-5 col-lg-3 col-xl-4">
                        <input
                            {...formik.getFieldProps('value')}
                            className={clsx(
                                'form-control',
                                { 'is-invalid': formik.touched.value && formik.errors.value },
                            )}
                            type='text'
                            name='value'
                        />
                        {formik.touched.value && formik.errors.value && (
                            <div className='fv-plugins-message-container text-danger'>
                                <span role='alert' className="text-danger">{formik.errors.value}</span>
                            </div>
                        )}
                    </div>
                </div>
                <div className="separator separator-dashed my-6"></div>
                <div className="row">
                    <label className="col-md-7 col-lg-9 col-xl-8 align-items-start">
                        <span className="form-check-label d-flex flex-column align-items-start">
                            <span className="fw-bolder fs-5 mb-0">{intl.formatMessage({ id: "PRICE_VARIANT" })}</span>
                            <span className="text-muted fs-6" style={{ maxWidth: '500px' }}>{intl.formatMessage({ id: "MASTER.PRICE_VARIANT.FORM.LABEL" }, { title: '' })}</span>
                        </span>
                    </label>
                    <div className="col-md-5 col-lg-3 col-xl-4">
                        <VirtualizedSelect
                            isDisabled={variantPriceIsLoading}
                            isLoading={variantPriceIsLoading}
                            placeholder={`${intl.formatMessage({ id: 'FORM.ACTION.CHOOSE' })} ${intl.formatMessage({ id: 'PRICE_VARIANT' })}`}
                            options={_variantPriceOption}
                            value={
                                _variantPriceOption.find(
                                    (option) => option.value === formik.values.variantPriceId
                                ) || null
                            }
                            onChange={(selectedOptions) => {
                                formik.setFieldValue('variantPriceId', selectedOptions?.value ?? null)
                            }}
                            // onBlur={
                            //     () => formik.values.sourceFilterDate && formik.values.sourceFilterDate.length === 0 && formik.setFieldTouched("sourceFilterDate", true)
                            // }
                            isClearable={true}
                        />
                        {/* {formik.touched.sourceFilterDate && formik.errors.sourceFilterDate && (
                            <div className='fv-plugins-message-container text-danger'>
                                <span role='alert' className="text-danger">{formik.errors.sourceFilterDate}</span>
                            </div>
                        )} */}
                    </div>
                </div>
                <div className="separator separator-dashed my-6"></div>
                <div className="row">
                    <label className="col-md-7 col-lg-9 col-xl-8 align-items-start">
                        <span className="form-check-label d-flex flex-column align-items-start">
                            <span className="fw-bolder fs-5 mb-0">{intl.formatMessage({ id: "MASTER.CURRENCIES.CURRENCY" })}</span>
                            <span className="text-muted fs-6" style={{ maxWidth: '500px' }}>{intl.formatMessage({ id: "MASTER.CURRENCIES.FORM.LABEL" }, { title: '' })}</span>
                        </span>
                    </label>
                    <div className="col-md-5 col-lg-3 col-xl-4">
                        <VirtualizedSelect
                            placeholder={`${intl.formatMessage({ id: 'FORM.ACTION.CHOOSE' })} ${intl.formatMessage({ id: 'MASTER.CURRENCIES.CURRENCY' })}`}
                            options={currencyOptions}
                            value={
                                currencyOptions.find(
                                    (option) => option.value === formik.values.currencyId
                                ) || null
                            }
                            onChange={(selectedOptions) => {
                                formik.setFieldValue('currencyId', selectedOptions?.value ?? '0')
                            }}
                            onBlur={
                                () => formik.values.sourceFilterDate && formik.values.sourceFilterDate.length === 0 && formik.setFieldTouched("sourceFilterDate", true)
                            }
                            isClearable={false}
                        />
                        {formik.touched.sourceFilterDate && formik.errors.sourceFilterDate && (
                            <div className='fv-plugins-message-container text-danger'>
                                <span role='alert' className="text-danger">{formik.errors.sourceFilterDate}</span>
                            </div>
                        )}
                    </div>
                </div>
                <div className="separator separator-dashed my-6"></div>
                <div className="row">
                    <label className="col-md-7 col-lg-9 col-xl-8 align-items-start">
                        <span className="form-check-label d-flex flex-column align-items-start">
                            <span className="fw-bolder fs-5 mb-0">{intl.formatMessage({ id: "FORM.LABEL.SETTING_TEMPLATE" }, { title: settings.merchant_title })}</span>
                            <span className="text-muted fs-6">{intl.formatMessage({ id: "FORM.LABEL.DESC_SETTING_MEETING" }, { title: settings.merchant_title, title2: settings.pipeline_title })}</span>
                        </span>
                    </label>
                    <div className="col-md-5 col-lg-3 col-xl-4">
                        <VirtualizedSelect
                            placeholder={`${intl.formatMessage({ id: 'FORM.ACTION.CHOOSE' })} ${intl.formatMessage({ id: 'FILTER.LABEL.DATABASE' })} ${settings.merchant_title}`}
                            value={formik.values.merchantTemplateIds as any}
                            options={dataMerchant}
                            onChange={(selectedOptions: VirtualizedSelectOption[]) => {
                                if (selectedOptions.length === 0) {
                                    formik.setFieldValue('merchantTemplateIds', allOptionMerchant)
                                } else {
                                    var endIndex = selectedOptions.length - 1
                                    if (selectedOptions[endIndex].value === 'all') {
                                        formik.setFieldValue('merchantTemplateIds', allOptionMerchant)
                                    } else {
                                        formik.setFieldValue('merchantTemplateIds', selectedOptions.filter(item => item.value !== 'all'))
                                    }
                                }
                            }}
                            isMulti
                            isClearable
                            isSort={false}
                            closeMenuOnSelect={false}
                        />
                    </div>
                </div>
                <div className="separator separator-dashed my-6"></div>
                <div className="row">
                    <label className="col-md-7 col-lg-9 col-xl-8 align-items-start">
                        <span className="form-check-label d-flex flex-column align-items-start">
                            <span className="fw-bolder fs-5 mb-0">{intl.formatMessage({ id: "FORM.LABEL.SETTING_TEMPLATE" }, { title: intl.formatMessage({ id: "FORM.LABEL.ROLE" }) })}</span>
                            <span className="text-muted fs-6">{intl.formatMessage({ id: "FORM.LABEL.DESC_SETTING_ROLE" })}</span>
                        </span>
                    </label>
                    <div className="col-md-5 col-lg-3 col-xl-4">
                        <VirtualizedSelect
                            placeholder={`${intl.formatMessage({ id: 'FORM.ACTION.CHOOSE' })} ${intl.formatMessage({ id: "FORM.LABEL.ROLE" })}`}
                            value={formik.values.roleIds as any}
                            options={dataRole}
                            onChange={(selectedOptions: VirtualizedSelectOption[]) => {
                                if (selectedOptions.length === 0) {
                                    formik.setFieldValue('roleIds', allOptionRole)
                                } else {
                                    var endIndex = selectedOptions.length - 1
                                    if (selectedOptions[endIndex].value === 'all') {
                                        formik.setFieldValue('roleIds', allOptionRole)
                                    } else {
                                        formik.setFieldValue('roleIds', selectedOptions.filter(item => item.value !== 'all'))
                                    }
                                }
                            }}
                            isMulti
                            isClearable
                            isSort={false}
                            closeMenuOnSelect={false}
                        />
                    </div>
                </div>
                <div className="separator separator-dashed my-6"></div>
                <div className="row">
                    <label className="col-md-7 col-lg-9 col-xl-8 align-items-start">
                        <span className="form-check-label d-flex flex-column align-items-start">
                            <span className="fw-bolder fs-5 mb-0">{intl.formatMessage({ id: "FORM.LABEL.SETTING_PRODUCTS" })}</span>
                            <span className="text-muted fs-6" style={{ maxWidth: '500px' }}>{intl.formatMessage({ id: "FORM.LABEL.DESC_SETTING_PRODUCTS" }, { title: settings.pipeline_title })}</span>
                        </span>
                    </label>
                    <div className="col-md-5 col-lg-3 col-xl-4">
                        <VirtualizedSelect
                            placeholder={`${intl.formatMessage({ id: 'FORM.ACTION.CHOOSE' })} ${intl.formatMessage({ id: 'FORM.LABEL.PRODUCTS' })}`}
                            options={dataProduct}
                            value={formik.values.accessProduct}
                            onChange={(selectedOptions: VirtualizedSelectOption[]) => {
                                if (selectedOptions.length === 0) {
                                    formik.setFieldValue('accessProduct', allOptionProduct)
                                } else {
                                    var endIndex = selectedOptions.length - 1
                                    if (selectedOptions.length > 0 && selectedOptions[endIndex].value === 'all') {
                                        formik.setFieldValue('accessProduct', allOptionProduct)
                                    } else {
                                        formik.setFieldValue('accessProduct', selectedOptions.filter(item => item.value !== 'all'))
                                    }
                                }
                            }}
                            isMulti
                            isSort={false}
                            closeMenuOnSelect={false}
                        />
                    </div>
                </div>

                <div className="separator separator-dashed my-6"></div>
                <div className="d-flex justify-content-between align-items-center gap-4">
                    <label className="col-md-7 col-lg-9 col-xl-8 form-check form-switch form-check-custom form-check-solid align-items-start cursor-pointer" onClick={() => formik.setFieldValue('isFeatureLabelEnabled', !formik.values.isFeatureLabelEnabled)}>
                        <span className="form-check-label d-flex flex-column align-items-start m-0">
                            <span className="fw-bolder fs-5 mb-0">{intl.formatMessage({ id: "FORM.LABEL.SETTING_LABEL" })}</span>
                            <span className="text-muted fs-6" style={{ maxWidth: '500px' }}>{intl.formatMessage({ id: "FORM.LABEL.DESC_SETTING_LABEL" })}</span>
                        </span>
                    </label>
                    <Toggle className="col-md-5 col-lg-3 col-xl-4" {...formik.getFieldProps('isFeatureLabelEnabled')} checked={formik.values.isFeatureLabelEnabled!} onChange={(e) => formik.setFieldValue('isFeatureLabelEnabled', e)} />
                    {formik.touched.isFeatureLabelEnabled && formik.errors.isFeatureLabelEnabled && (
                        <div className='fv-plugins-message-container text-danger'>
                            <span role='alert' className="text-danger">{formik.errors.isFeatureLabelEnabled}</span>
                        </div>
                    )}
                </div>
                <div className="separator separator-dashed my-6"></div>
                <div className="d-flex justify-content-between align-items-center gap-4">
                    <label className="col-md-7 col-lg-9 col-xl-8 form-check form-switch form-check-custom form-check-solid align-items-start cursor-pointer" onClick={() => formik.setFieldValue('isFeatureCogsEnabled', !formik.values.isFeatureCogsEnabled)}>
                        <span className="form-check-label d-flex flex-column align-items-start m-0">
                            <span className="fw-bolder fs-5 mb-0">{intl.formatMessage({ id: "FORM.LABEL.SETTING_COGS" })}</span>
                            <span className="text-muted fs-6" style={{ maxWidth: '500px' }}>{intl.formatMessage({ id: "FORM.LABEL.DESC_SETTING_COGS" })}</span>
                        </span>
                    </label>
                    <Toggle className="col-md-5 col-lg-3 col-xl-4" {...formik.getFieldProps('isFeatureCogsEnabled')} checked={formik.values.isFeatureCogsEnabled!} onChange={(e) => formik.setFieldValue('isFeatureCogsEnabled', e)} />
                    {formik.touched.isFeatureCogsEnabled && formik.errors.isFeatureCogsEnabled && (
                        <div className='fv-plugins-message-container text-danger'>
                            <span role='alert' className="text-danger">{formik.errors.isFeatureCogsEnabled}</span>
                        </div>
                    )}
                </div>

                {/* Optional Product */}
                <div className="separator separator-dashed my-6"></div>
                <div className="d-flex justify-content-between align-items-center gap-4">
                    <label className="col-md-7 col-lg-9 col-xl-8 form-check form-switch form-check-custom form-check-solid align-items-start cursor-pointer" onClick={() => formik.setFieldValue('allowOptionalProducts', !formik.values.allowOptionalProducts)}>
                        <span className="form-check-label d-flex flex-column align-items-start m-0">
                            <span className="fw-bolder fs-5 mb-0">{intl.formatMessage({ id: "FORM.LABEL.SETTING_OPTIONAL_PRODUCT" })}</span>
                            <span className="text-muted fs-6" style={{ maxWidth: '500px' }}>{intl.formatMessage({ id: "FORM.LABEL.DESC_SETTING_OPTIONAL_PRODUCT" })}</span>
                        </span>
                    </label>
                    <Toggle className="col-md-5 col-lg-3 col-xl-4" {...formik.getFieldProps('allowOptionalProducts')} checked={formik.values.allowOptionalProducts!} onChange={(e) => formik.setFieldValue('allowOptionalProducts', e)} />
                    {formik.touched.allowOptionalProducts && formik.errors.allowOptionalProducts && (
                        <div className='fv-plugins-message-container text-danger'>
                            <span role='alert' className="text-danger">{formik.errors.allowOptionalProducts}</span>
                        </div>
                    )}
                </div>

                {/* Disabled Price Product */}
                <div className="separator separator-dashed my-6"></div>
                <div className="d-flex justify-content-between align-items-center gap-4">
                    <label className="col-md-7 col-lg-9 col-xl-8 form-check form-switch form-check-custom form-check-solid align-items-start cursor-pointer" onClick={() => formik.setFieldValue('isDisabledPriceProduct', !formik.values.isDisabledPriceProduct)}>
                        <span className="form-check-label d-flex flex-column align-items-start m-0">
                            <span className="fw-bolder fs-5 mb-0">{intl.formatMessage({ id: "FORM.LABEL.SETTING_DISABLED_PRICE_PRODUCT" })}</span>
                            <span className="text-muted fs-6" style={{ maxWidth: '500px' }}>{intl.formatMessage({ id: "FORM.LABEL.DESC_SETTING_DISABLED_PRICE_PRODUCT" })}</span>
                        </span>
                    </label>
                    <Toggle className="col-md-5 col-lg-3 col-xl-4" {...formik.getFieldProps('isDisabledPriceProduct')} checked={formik.values.isDisabledPriceProduct!} onChange={(e) => formik.setFieldValue('isDisabledPriceProduct', e)} />
                    {formik.touched.isDisabledPriceProduct && formik.errors.isDisabledPriceProduct && (
                        <div className='fv-plugins-message-container text-danger'>
                            <span role='alert' className="text-danger">{formik.errors.isDisabledPriceProduct}</span>
                        </div>
                    )}
                </div>

                {/* Allow Source */}
                <div className="separator separator-dashed my-6"></div>
                <div className="d-flex justify-content-between align-items-center gap-4">
                    <label className="col-md-7 col-lg-9 col-xl-8 form-check form-switch form-check-custom form-check-solid align-items-start cursor-pointer" onClick={() => formik.setFieldValue('', !formik.values.leadSourcesId)}>
                        <span className="form-check-label d-flex flex-column align-items-start m-0">
                            <span className="fw-bolder fs-5 mb-0">{intl.formatMessage({ id: "FORM.LABEL.SETTING_SOURCE" })}</span>
                            <span className="text-muted fs-6" style={{ maxWidth: '500px' }}>{intl.formatMessage({ id: "FORM.LABEL.DESC_SETTING_SOURCE" }, { title: settings.pipeline_title })}</span>
                        </span>
                    </label>
                    <div className="col-md-5 col-lg-3 col-xl-4">
                        <VirtualizedSelect
                            placeholder={`${intl.formatMessage({ id: 'FORM.ACTION.CHOOSE' })} ${intl.formatMessage({ id: 'FORM.LABEL.SOURCE' })}`}
                            options={dataSource}
                            onChange={(selectedOptions: VirtualizedSelectOption[]) => {
                                if (selectedOptions.length === 0) {
                                    formik.setFieldValue('leadSourcesId', allOptionSource)
                                } else {
                                    var endIndex = selectedOptions.length - 1
                                    if (selectedOptions.length > 0 && selectedOptions[endIndex].value === 'all') {
                                        formik.setFieldValue('leadSourcesId', allOptionSource)
                                    } else {
                                        formik.setFieldValue('leadSourcesId', selectedOptions.filter(item => item.value !== 'all'))
                                    }
                                }
                            }}
                            value={formik.values.leadSourcesId as any}
                            isMulti
                            isSort={false}
                            closeMenuOnSelect={false}
                        />
                        {formik.touched.leadSourcesId && formik.errors.leadSourcesId && (
                            <div className='fv-plugins-message-container text-danger'>
                                <span role='alert' className="text-danger">{formik.errors.leadSourcesId}</span>
                            </div>
                        )}
                    </div>
                </div>
                {/* Progress */}
                <div className="separator separator-dashed my-6"></div>
                <div className="row">
                    <label className="col-md-7 col-lg-9 col-xl-8 align-items-start">
                        <span className="form-check-label d-flex flex-column align-items-start">
                            <span className="fw-bolder fs-5 mb-0">{intl.formatMessage({ id: "FORM.LABEL.SETTING_PROGRESS" })}</span>
                            <span className="text-muted fs-6" style={{ maxWidth: '500px' }}>{intl.formatMessage({ id: "FORM.LABEL.DESC_SETTING_PROGRESS" }, { title: settings.pipeline_title })}</span>
                        </span>
                    </label>
                    <div className="col-md-5 col-lg-3 col-xl-4">
                        <VirtualizedSelect
                            placeholder={`${intl.formatMessage({ id: 'FORM.ACTION.CHOOSE' })} ${intl.formatMessage({ id: 'FORM.LABEL.PROGRESS' })}`}
                            options={dataProgres}
                            onChange={(selectedOptions: VirtualizedSelectOption[]) => {
                                if (selectedOptions.length === 0) {
                                    formik.setFieldValue('accessProgress', allOptionProgress)
                                } else {
                                    var endIndex = selectedOptions.length - 1
                                    if (selectedOptions.length > 0 && selectedOptions[endIndex].value === 'all') {
                                        formik.setFieldValue('accessProgress', allOptionProgress)
                                    } else {
                                        formik.setFieldValue('accessProgress', selectedOptions.filter(item => item.value !== 'all'))
                                    }
                                }
                            }}
                            value={formik.values.accessProgress as any}
                            isMulti
                            isSort={false}
                            closeMenuOnSelect={false}
                        />
                    </div>
                </div>
                <div className="separator separator-dashed my-6"></div>
                {/* Add Lead Log Success*/}
                <div className="d-flex justify-content-between align-items-center gap-4">
                    <label className="col-md-7 col-lg-9 col-xl-8 form-check form-switch form-check-custom form-check-solid align-items-start cursor-pointer" onClick={() => formik.setFieldValue('isAllowAfterSuccessLog', !formik.values.isAllowAfterSuccessLog)}>
                        <span className="form-check-label d-flex flex-column align-items-start m-0">
                            <span className="fw-bolder fs-5 mb-0">{intl.formatMessage({ id: "FORM.LABEL.SETTING_ADD_LEAD_LOG_SUCCESS" })}</span>
                            <span className="text-muted fs-6" style={{ maxWidth: '500px' }}>{intl.formatMessage({ id: "FORM.LABEL.DESC_SETTING_ADD_LEAD_LOG_SUCCESS" })}</span>
                        </span>
                    </label>
                    <Toggle className="col-md-5 col-lg-3 col-xl-4" {...formik.getFieldProps('isAllowAfterSuccessLog')} checked={formik.values.isAllowAfterSuccessLog!} onChange={(e) => formik.setFieldValue('isAllowAfterSuccessLog', e)} />
                    {formik.touched.isAllowAfterSuccessLog && formik.errors.isAllowAfterSuccessLog && (
                        <div className='fv-plugins-message-container text-danger'>
                            <span role='alert' className="text-danger">{formik.errors.isAllowAfterSuccessLog}</span>
                        </div>
                    )}
                </div>
                <div className="separator separator-dashed my-6"></div>
                {/* Add Lead Log Failed*/}
                <div className="d-flex justify-content-between align-items-center gap-4">
                    <label className="col-md-7 col-lg-9 col-xl-8 form-check form-switch form-check-custom form-check-solid align-items-start cursor-pointer" onClick={() => formik.setFieldValue('isAllowAfterFailedLog', !formik.values.isAllowAfterFailedLog)}>
                        <span className="form-check-label d-flex flex-column align-items-start m-0">
                            <span className="fw-bolder fs-5 mb-0">{intl.formatMessage({ id: "FORM.LABEL.SETTING_ADD_LEAD_LOG_FAILED" })}</span>
                            <span className="text-muted fs-6" style={{ maxWidth: '500px' }}>{intl.formatMessage({ id: "FORM.LABEL.DESC_SETTING_ADD_LEAD_LOG_FAILED" })}</span>
                        </span>
                    </label>
                    <Toggle className="col-md-5 col-lg-3 col-xl-4" {...formik.getFieldProps('isAllowAfterFailedLog')} checked={formik.values.isAllowAfterFailedLog!} onChange={(e) => formik.setFieldValue('isAllowAfterFailedLog', e)} />
                    {formik.touched.isAllowAfterFailedLog && formik.errors.isAllowAfterFailedLog && (
                        <div className='fv-plugins-message-container text-danger'>
                            <span role='alert' className="text-danger">{formik.errors.isAllowAfterFailedLog}</span>
                        </div>
                    )}
                </div>
                <div className="separator separator-dashed my-6"></div>
                <div className="row">
                    <label className="col-md-7 col-lg-9 col-xl-8 align-items-start">
                        <span className="form-check-label d-flex flex-column align-items-start">
                            <span className="fw-bolder fs-5 mb-0">{intl.formatMessage({ id: "STUDIO.LABEL.SOURCE_DATE_FORM" })}</span>
                            <span className="text-muted fs-6" style={{ maxWidth: '500px' }}>{intl.formatMessage({ id: "STUDIO.LABEL.SOURCE_DATE_FORM_DESC" }, { title: settings.pipeline_title })}</span>
                        </span>
                    </label>
                    <div className="col-md-5 col-lg-3 col-xl-4">
                        <VirtualizedSelect
                            placeholder={`${intl.formatMessage({ id: 'FORM.ACTION.CHOOSE' })} ${intl.formatMessage({ id: 'STUDIO.LABEL.SOURCE_DATE_LABEL' })}`}
                            options={sourceFilterDateOption}
                            value={sourceFilterDateOption.find((option) => option.value === formik.values.sourceFilterDate)}
                            onChange={(selectedOptions) => {
                                formik.setFieldValue('sourceFilterDate', selectedOptions?.value ?? '')
                            }}
                            onBlur={
                                () => formik.values.sourceFilterDate && formik.values.sourceFilterDate.length === 0 && formik.setFieldTouched("sourceFilterDate", true)
                            }
                            isClearable={false}
                        />
                        {formik.touched.sourceFilterDate && formik.errors.sourceFilterDate && (
                            <div className='fv-plugins-message-container text-danger'>
                                <span role='alert' className="text-danger">{formik.errors.sourceFilterDate}</span>
                            </div>
                        )}
                    </div>
                </div>
                {formik.values.sourceFilterDate !== 'NONE' && (
                    <>
                        <div className="separator separator-dashed my-6" />
                        <div className="d-flex gap-8 align-items-start">
                            <div className="my-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" className="bi bi-arrow-return-right" viewBox="0 0 16 16">
                                    <path fill-rule="evenodd" d="M1.5 1.5A.5.5 0 0 0 1 2v4.8a2.5 2.5 0 0 0 2.5 2.5h9.793l-3.347 3.346a.5.5 0 0 0 .708.708l4.2-4.2a.5.5 0 0 0 0-.708l-4-4a.5.5 0 0 0-.708.708L13.293 8.3H3.5A1.5 1.5 0 0 1 2 6.8V2a.5.5 0 0 0-.5-.5" />
                                </svg>
                            </div>
                            <div className="row w-100">
                                <label className="col-md-7 col-lg-9 col-xl-8 align-items-start">
                                    <span className="form-check-label d-flex flex-column align-items-start" >
                                        <span className="fw-bolder fs-5 mb-0">{intl.formatMessage({ id: "STUDIO.LABEL.RANGE_DATE_FORM" })}</span>
                                        <span className="text-muted fs-6" style={{ maxWidth: '500px' }}>{intl.formatMessage({ id: "STUDIO.LABEL.RANGE_DATE_FORM_DESC" }, { title: settings.pipeline_title })}</span>
                                    </span>
                                </label>
                                <div className="col-md-5 col-lg-3 col-xl-4">
                                    <VirtualizedSelect
                                        placeholder={`${intl.formatMessage({ id: 'FORM.ACTION.CHOOSE' })} ${intl.formatMessage({ id: 'STUDIO.LABEL.RANGE_DATE_LABEL' })}`}
                                        value={filterDateOption.find((option) => option.value === formik.values.filterDate)}
                                        options={filterDateOption}
                                        onChange={(selectedOptions: any) => {
                                            formik.setFieldValue('filterDate', selectedOptions.value)
                                        }}
                                        onBlur={
                                            () => formik.values.filterDate && formik.values.filterDate.length === 0 && formik.setFieldTouched("filterDate", true)
                                        }
                                        isClearable={false}
                                    />
                                    {formik.touched.filterDate && formik.errors.filterDate && (
                                        <div className='fv-plugins-message-container text-danger'>
                                            <span role='alert' className="text-danger">{formik.errors.filterDate}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
            <div className="card-footer d-flex justify-content-end py-6 px-9 mt-6">
                <button type="submit" className={`btn btn-primary ${formik.isSubmitting ? "disabled" : ""}`} data-kt-indicator={formik.isSubmitting ? 'on' : 'off'}>
                    <span className="indicator-label">
                        {intl.formatMessage({ id: "FORM.ACTION.SAVE" })}
                    </span>
                    <span className="indicator-progress">
                        Loading... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                    </span>
                </button>
            </div>
        </form>
    )
}

export { PreferencePipeline }